import React from "react";
import CheckIcon from "@mui/icons-material/Check";
import WorkIcon from "@mui/icons-material/Work";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { getCardColor2 } from "../../../helpers/helpers";

function UserProfileAttributesTab(props) {
  const hasRoles = props.selectedTeamMemberData?.roles_and_role_types?.length > 0;
  const sortedRoles = hasRoles
    ? props.selectedTeamMemberData?.roles_and_role_types?.sort((a, b) => {
        if (a.scheduled_role_obj?.role_type_id === b.scheduled_role_obj?.role_type_id) {
          return a.scheduled_role_obj?.role_name.localeCompare(b.scheduled_role_obj?.role_name);
        }
        return a.scheduled_role_obj?.role_type_id - b.scheduled_role_obj?.role_type_id;
      })
    : [];

  return (
    <>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-violet-700 rounded-xl flex items-center justify-center shadow-lg">
              <WorkIcon className="text-white" style={{ fontSize: "24px" }} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">My Attributes</h2>
              <p className="text-gray-600">View your assigned roles and work preferences</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-8">
          {/* Roles Card */}
          <div className="col-span-2">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Card Header */}
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
                <div className="flex items-center space-x-3">
                  <h3 className="text-lg font-semibold text-gray-900">Assigned Roles</h3>
                </div>
              </div>

              {/* Card Content */}
              <div className="p-6">
                {hasRoles ? (
                  <div className="space-y-4">
                    {/* Table Header */}
                    <div className="grid grid-cols-12 gap-4 pb-3 border-b border-gray-100">
                      <div className="col-span-8">
                        <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">Role</span>
                      </div>
                      <div className="col-span-4 text-right">
                        <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">Main Role</span>
                      </div>
                    </div>

                    {/* Role Items */}
                    <div className="space-y-3">
                      {sortedRoles.map((role, index) => (
                        <div key={index} className="grid grid-cols-12 gap-4 items-center py-3 rounded-lg px-3 transition-colors">
                          {/* Role Name */}
                          <div className="col-span-8 flex items-center space-x-3">
                            <div
                              className={`w-4 h-4 rounded-full shadow-sm ${getCardColor2(
                                role.scheduled_role,
                                props.roleNames,
                                false
                              )}`}
                            />
                            <span className="font-medium text-gray-900">
                              {role?.scheduled_role_obj?.role_name}
                            </span>
                          </div>

                          {/* Primary Role Indicator */}
                          <div className="col-span-4 flex justify-end">
                            {role?.main_role_ind === 1 ? (
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-medium text-emerald-700">Primary</span>
                                <div className="w-7 h-7 rounded-full bg-emerald-600 flex items-center justify-center shadow-sm">
                                  <CheckIcon className="text-white" style={{ fontSize: "16px" }} />
                                </div>
                              </div>
                            ) : (
                              <div className="w-7 h-7 rounded-full border-2 border-gray-200 bg-white"></div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  /* Empty State */
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <WorkIcon className="text-gray-400" style={{ fontSize: "32px" }} />
                    </div>
                    <h4 className="text-lg font-medium text-gray-900 mb-2">No roles assigned</h4>
                    <p className="text-gray-500">You haven't been assigned to any roles yet. Contact your manager for role assignment.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Target Hours Card */}
          <div className="col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden h-fit">
              {/* Card Header */}
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
                <div className="flex items-center space-x-3">
                  <h3 className="text-lg font-semibold text-gray-900">Target Hours</h3>
                </div>
              </div>

              {/* Card Content */}
              <div className="p-6">
                <div className="text-center">
                  <div className="mb-4">
                    <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">Weekly Target Hours</span>
                  </div>

                  {props.selectedTeamMemberData?.target_weekly_hours ? (
                    <div className="space-y-2">
                      <div className="text-4xl font-bold text-gray-900">
                        {props.selectedTeamMemberData.target_weekly_hours}
                      </div>
                      <div className="text-sm text-gray-500">hours per week</div>

                      {/* Visual progress indicator */}
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                          <span>0 hrs</span>
                          <span>40 hrs</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{
                              width: `${Math.min((props.selectedTeamMemberData.target_weekly_hours / 40) * 100, 100)}%`
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="py-8">
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <AccessTimeIcon className="text-gray-400" style={{ fontSize: "24px" }} />
                      </div>
                      <div className="text-gray-500 text-sm">No target hours set</div>
                      <div className="mt-2 text-xs text-gray-400">Contact your manager to set target hours</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default UserProfileAttributesTab;
